import React from "react"
import { type ApolloError } from "@apollo/client"
import { type Transaction } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgLayersTwo02 } from "@digits-shared/components/SVGIcons/line/LayersTwo02.svg"
import { type ButtonProps, DigitsButton } from "@digits-shared/DesignSystem/Button"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { type FrontendTransaction } from "src/frontend/components/OS/Applications/Accounting/TransactionDetail/state/types"
import { useHasMatchButtonsPermissions } from "src/frontend/components/OS/Applications/Shared/Accounting/Matching/hooks/useCanUseMatchButtons"
import { useTransactionsCanBeUnmatched } from "src/frontend/components/OS/Applications/Shared/Accounting/Matching/hooks/useTransactionsCanBeUnmatched"
import { UnmatchModal } from "src/frontend/components/OS/Applications/Shared/Accounting/Matching/UnmatchModal"

/*
  STYLES
*/

const Icon = styled(SvgLayersTwo02)`
  ${svgPathStyles(colors.secondary, 1)};
  g {
    ${svgPathStyles(colors.secondary, 1)};
  }
`

/*
  INTERFACES
*/

interface UnmatchTooltipProps extends React.PropsWithChildren {
  reason: string
  valid: boolean
}

/*
  COMPONENTS
*/

interface Props extends ButtonProps {
  transactions: Transaction[] | FrontendTransaction
  onUnmatch?: (transactions: Transaction[] | FrontendTransaction) => void
  onError?: (error: ApolloError) => void
}

/*
  COMPONENTS
*/

export const UnmatchButton: React.FC<Props> = ({
  transactions,
  onUnmatch,
  onError,
  ...buttonProps
}) => {
  const hasUnmatchButtonPermission = useHasMatchButtonsPermissions()
  const { valid, reason, isMatched, groupedMatches } = useTransactionsCanBeUnmatched(transactions)
  const { setTrue: showModal, setFalse: hideModal, value: modalVisible } = useStateBoolean()

  const onUnmatchWithClose = React.useCallback(() => {
    onUnmatch?.(transactions)
    hideModal()
  }, [hideModal, onUnmatch, transactions])

  const onErrorWithClose = React.useCallback(
    (error: ApolloError) => {
      onError?.(error)
      hideModal()
    },
    [hideModal, onError]
  )

  if (!isMatched || !hasUnmatchButtonPermission || groupedMatches.size !== 1) {
    return null
  }

  return (
    <UnmatchTooltip reason={reason} valid={valid}>
      <DigitsButton
        onClick={showModal}
        disabled={!valid}
        $circle
        $variant="secondary-light"
        {...buttonProps}
      >
        <Icon />
      </DigitsButton>
      {modalVisible && (
        <UnmatchModal
          transactions={transactions}
          onUnmatch={onUnmatchWithClose}
          onError={onErrorWithClose}
          onCancel={hideModal}
        />
      )}
    </UnmatchTooltip>
  )
}

const UnmatchTooltip: React.FC<UnmatchTooltipProps> = ({ children, reason, valid }) => (
  <DigitsTooltip
    maxWidth={296}
    content={
      valid ? (
        "Unmatch"
      ) : (
        <div>
          <strong>Unmatching not available</strong>
          <br />
          {reason}
        </div>
      )
    }
  >
    {children}
  </DigitsTooltip>
)
